import { sgg } from "ml-savitzky-golay-generalized";

/**
 * Validated wrapper around `sgg` (ml-savitzky-golay-generalized) that returns
 * the smoothed signal. Matches scipy's `savgol_filter(mode='interp')`,
 * including the asymmetric edge fits, to floating-point precision (~1e-13 on
 * uniform fixtures) — checked by the co-located scipy-fixture tests.
 *
 * `timeSeconds` does not affect the output. Both scipy and `sgg` treat samples
 * as equally spaced in the fit, and for `derivative: 0` `sgg` divides each
 * fitted value by `deltaX ** 0 === 1`, so spacing cancels. We pass a constant
 * `deltaX` of `1` instead of `timeSeconds`: bit-for-bit identical, but it skips
 * `sgg`'s per-window mean-Δt loop. `timeSeconds` is kept only to validate its
 * length against `values` and to match the other filters' signature.
 *
 * Non-finite inputs are NOT screened — the per-call O(n) scan isn't worth it in
 * the streamer's hot path. One `NaN`/`Infinity` taints a window's worth of
 * outputs; callers must clean their signal upstream.
 *
 * Validation intentionally diverges from scipy:
 *   - `polynomial >= 1` (scipy accepts `0`). `sgg` also rejects `0`; we mirror
 *     that floor with a friendlier message.
 *   - `polynomial <= 5`. `sgg` only `console.warn`s above `5`; we hard-fail so
 *     it can't slip through silently in production.
 *   - `windowSize >= 5`. `sgg` enforces this too, but reifying the floor keeps
 *     a `sgg` version bump from silently regressing it and gives direct callers
 *     (via `shared/math/filters`) a clear message.
 *
 * Every error thrown here is an `Error` prefixed `applySavgol:`, including any
 * `RangeError` from `sgg` (caught and re-thrown below).
 *
 * When invoked via the expression-evaluator, series shorter than `windowSize`
 * are passed through before reaching this function; the `values.length <
 * windowSize` guard below covers direct callers.
 */
export function applySavgol(
  values: number[],
  timeSeconds: number[],
  windowSize: number,
  polynomial: number,
): number[] {
  if (values.length !== timeSeconds.length) {
    throw new Error(
      `applySavgol: values and timeSeconds must have the same length (got ${values.length} and ${timeSeconds.length})`,
    );
  }
  if (windowSize % 2 === 0) {
    throw new Error(`applySavgol: windowSize must be odd (got ${windowSize})`);
  }
  if (windowSize < 5) {
    throw new Error(
      `applySavgol: windowSize must be >= 5 (got ${windowSize}); the underlying sgg library rejects smaller windows`,
    );
  }
  if (polynomial < 1 || !Number.isInteger(polynomial)) {
    throw new Error(
      `applySavgol: polynomial must be a positive integer (got ${polynomial}); note this diverges from scipy.savgol_filter, whose polyorder accepts 0`,
    );
  }
  if (polynomial > 5) {
    throw new Error(
      `applySavgol: polynomial must be <= 5 (got ${polynomial}); higher polynomial degrees are not recommended by the underlying sgg library`,
    );
  }
  if (polynomial >= windowSize) {
    throw new Error(
      `applySavgol: polynomial (${polynomial}) must be less than windowSize (${windowSize})`,
    );
  }
  if (values.length < windowSize) {
    throw new Error(
      `applySavgol: values length (${values.length}) is shorter than windowSize (${windowSize})`,
    );
  }

  // Constant `deltaX` of `1` (spacing cancels for derivative 0; see JSDoc).
  let result: ArrayLike<number>;
  try {
    result = sgg(values, 1, {
      windowSize,
      polynomial,
      derivative: 0,
    });
  } catch (e) {
    // Re-throw sgg's own RangeErrors (e.g. a bad windowSize from a JS caller
    // that bypassed the TS types) with the wrapper's prefix, so every error
    // out of applySavgol shares one shape.
    const cause = e instanceof Error ? e.message : String(e);
    throw new Error(`applySavgol: ${cause}`);
  }
  return Array.from(result);
}
